<script setup>
    import { useMyOhcStore } from '@/stores/my-ohc';
    import { useRouteQuery } from '@vueuse/router';
    const { signOut, isLoggedIn, user } = useAuth()
    
    const myOhcStore = useMyOhcStore();
    const links = myOhcStore.myOhcLinks;

    const route = useRoute();

    // Derived from the URL, not held here (Asana O2-53). This used to be its own
    // `ref(true)` while Templates/my-ohc/page.vue — which decides what actually
    // renders — defaults to `useRouteQuery('mapView', false)`. So arriving at
    // My OHC lit the map icon while the list was on screen, and reloading with
    // ?mapView=true did the reverse. One source of truth instead.
    //
    // Normalised because @vueuse/router hands back the raw query STRING when the
    // param is present and the default when it is absent, so this is `false`,
    // `'true'` or `'false'` — and `'false'` is truthy.
    const mapViewQuery = useRouteQuery('mapView', false);
    const mapView = computed(() => mapViewQuery.value === true || mapViewQuery.value === 'true');
    const ohcIndex = ref(0);

    const emit = defineEmits(['viewToggle']);
    
    defineExpose({
        viewToggle,
    });    

    function viewToggle(val) {
        emit('viewToggle', val);
    }

    function isActiveUrl(url) {
        let active = false;
        if (route.path === url) active = true;
        if (route.path === url + '/') active = true;
        if (route.path === '/iframe' + url) active = true;
        if (route.path === '/iframe' + url + '/') active = true;
        return active;
    }

    function isPrimaryNavActive(url) {
        let state = 'favorites';
        if (url === '/my-ohc/itineraries') state = 'itineraries';
        if (url === '/my-ohc/itineraries/') state = 'itineraries';
        if (url === '/iframe/my-ohc/itineraries') state = 'itineraries';
        if (url === '/iframe/my-ohc/itineraries/') state = 'itineraries';
        return state;
    }

</script>


<template>
    <nav class="my-ohc-nav container hd-container" id="my-ohc-nav">
        <ClientOnly>
            <div class="login mobile-only" v-if="!isLoggedIn">
                <NuxtLink to="/login"><SvgsMy class="my-ohc-icon" /> Log in to My OHC </NuxtLink>
            </div>
            <div v-else class="login mobile-only logout">
                <p>Logged in as <strong>{{ user.email }}</strong></p>
                <button type="button" @click.prevent="signOut">
                    Log out
                </button>
            </div>
        </ClientOnly> 
        <ul class="primary-nav tabs">
            <li class="top-item" :class="{ active: isPrimaryNavActive(route.path) == 'favorites' }">
                <NuxtLink to="/my-ohc/"> My Favorites </NuxtLink>
            </li>
            <li class="top-item" :class="{ active: isPrimaryNavActive(route.path) == 'itineraries' }">
                <NuxtLink to="/my-ohc/itineraries"> My Itineraries </NuxtLink>
            </li>
            <ClientOnly>
                <li class="login" :class="{ logout: isLoggedIn }">
                    <template v-if="!isLoggedIn">
                        <NuxtLink to="/login"><SvgsMy class="my-ohc-icon" /> Log in to My OHC </NuxtLink>
                    </template>
                    <template v-else>
                        <p>Logged in as <strong>{{ user.email }}</strong></p>
                        <button type="button" @click.prevent="signOut">
                            Log out
                        </button>
                    </template>
                </li>
            </ClientOnly>
        </ul>
        <div v-if="'/my-ohc/itineraries' !== route.path" class="sub-nav-wrapper">
            <ul class="sub-nav">
                <li v-for="(child, childIndex) in links[ohcIndex].children" :key="childIndex" :class="{ active: isActiveUrl(child.url) }">
                    <NuxtLink :to="child.url">
                        {{ child.name }}
                    </NuxtLink>
                </li>
            </ul>
            <ul class="map-list-buttons" v-if="route.path !== '/my-ohc/site-tours/'">
                <li>
                    <button class="map view-button" @click.prevent="viewToggle(true)" aria-label="Map View">
                        <SvgsMapFolded class="map-folded" :class="{ active: mapView }" />
                    </button>
                </li>
                <li>
                    <button class="list view-button" @click.prevent="viewToggle(false)" aria-label="List View">
                        <SvgsList class="list" :class="{ active: !mapView }" />
                    </button>
                </li>
            </ul>
        </div>
    </nav>
</template>

<style lang="scss">
    .my-ohc-nav {
        margin-bottom: 16px;

        .login {
            display: none;
            
            @media (min-width: $tablet) {
                display: flex;
            }            
        }

        .login.mobile-only {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-bottom: 16px;

            @media (min-width: $tablet) {
                display: none;
            }
        }

        .logout {
            font-size: 13px;
            gap: 16px;

            button {
                color: $red;
                font-weight: 500;

                &:hover {
                    color: $turquoise;
                }
            }
        }

        .primary-nav {
            display: flex;
            margin-bottom: 18px;
            
            &.tabs {
                margin-top: 0;
            }

            .login, .login.logout {
                display: none;

                @media (min-width: $tablet) {
                    display: flex;
                }
            }

            > li {
                flex: 0 0 50%;
                text-align: center;
                border: 1px solid $gray-common;
                &:last-of-type {
                    border-left: 0;
                }

                @media (min-width: $tablet) {
                    flex: none;
                    margin-right: 15px;

                    &:last-of-type {
                        border-left: 1px solid $gray-common;
                    }
                }

                button,
                a {
                    display: block;
                    width: 100%;
                    height: 100%;
                    padding: 10px 8px 6px;
                    background-color: $white;
                    color: $black;
                    text-transform: uppercase;

                    @media (min-width: $tablet) {
                        padding: 10px 50px 6px;
                    }
                }

                &.active {
                    button,
                    a {
                        background-color: $black;
                        color: $white;
                    }
                }

                &.login {
                    border: none;
                    margin-left: auto;
                    margin-right: 0;
                    border: none;                                        
                    display: none;

                    @media (min-width: $tablet) {
                        display: flex;
                    }                    

                    a, button {
                        display: flex;
                        align-items: center;                        
                        gap: 13px;                        
                        text-transform: none;
                        padding-left: 0;
                        padding-right: 0;
                    }
                }

                &.logout {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 16px;
                    
                    p {
                        padding: 10px 0 6px 0;
                    }
                    
                    button {
                        width: auto;
                        padding: 10px 0 6px 0;
                        color: $red;
                        
                        &:hover {
                            color: $turquoise;
                        }
                    }
                }
            }
        }

        .login {

            font-style: normal;
            line-height: 1em;

            .my-ohc-icon {
                width: 14px;
                height: 17px;
            }

            a {
                display: flex;
                align-items: center;                        
                gap: 13px;
                font-size: 13px;
                line-height: 1em;
                font-weight: 500;

                &:hover {
                    color: $red;

                    svg {
                        fill: $red;
                        path {
                            stroke: $red;
                        }
                    }
                }
            }
        }        

        .sub-nav-wrapper {
            display: flex;

            .map-list-buttons {
                display: flex;
                align-items: center;
                margin-left: auto;

                > li:first-of-type {
                    margin-right: 15px;

                    @media (min-width: $tablet) {
                        margin-right: 25px;
                    }
                }

                @media (min-width: $tablet) {
                    .view-button svg {
                        width: 30px;
                        height: 30px;
                    }
                }
            }
        }

        .sub-nav {
            display: flex;
            margin: 0;
            gap: 16px;

            > li {
                background-color: $white;
                text-align: center;

                button,
                a {
                    display: block;
                    padding: 10px 8px 6px;
                    background-color: $gray-light;
                    color: $black;
                    font-size: 12px;
                    font-weight: 800;
                    line-height: 1;
                    letter-spacing: 0.96px;
                    text-transform: uppercase;
                }

                &.active {
                    button,
                    a {
                        background-color: $black;
                        color: $white;
                    }
                }
            }
        }
    }
</style>
